import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { KitButton } from 'components/common/cms-kit';

const OffTaskDialog = (props) => {
  const theme = useTheme();
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (props.open && inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [props.open]);

  const handleClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      props.closeConfirm && props.closeConfirm();
    }
    return;
  };

  const setOffTaskStudentCount = (num) => {
    let updatedNumber = 0;
    if (props.offTaskNumber === '' || props.offTaskNumber < 0) {
      updatedNumber = 0;
    } else {
      updatedNumber = parseInt(props.offTaskNumber, 10) + num;
    }
    if (
      typeof updatedNumber !== 'number' ||
      isNaN(updatedNumber) ||
      updatedNumber < 0
    ) {
      props.setOffTaskNumber(0);
      props.setOffTaskNumberError(true);
    } else {
      props.setOffTaskNumber(updatedNumber);
      props.setOffTaskNumberError(false);
    }
  };

  const updateOffTaskCount = (val) => {
    const updatedNumber = parseInt(val, 10);
    if (
      typeof updatedNumber !== 'number' ||
      isNaN(updatedNumber) ||
      updatedNumber < 0
    ) {
      props.setOffTaskNumber('');
      props.setOffTaskNumberError(true);
    } else {
      props.setOffTaskNumber(updatedNumber);
      props.setOffTaskNumberError(false);
    }
  };

  const canSave = React.useMemo(() => {
    return (
      props.offTaskNumber !== '' &&
      props.offTaskNumber >= 0 &&
      props.offTaskNumber <= props.studentCount
    );
  }, [props.offTaskNumber, props.studentCount]);

  return (
    <Dialog
      scroll="body"
      onClose={handleClose}
      aria-labelledby="off-task-dialog"
      open={props.open}
    >
      <DialogTitle
        id="off-task-dialog"
        sx={{
          backgroundColor: theme.palette['obsOff Task'].main,
          color: theme.palette.common.white,
          padding: '8px 16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            <PriorityHighIcon
              style={{
                marginLeft: '10px',
                marginTop: '-3px',
                fontSize: '1.75rem',
                fontWeight: 600,
                color: theme.palette.common.white,
              }}
            />{' '}
            Off Task Students
          </span>
          <IconButton
            onClick={() => {
              props.addToTimeline(true);
            }}
            sx={{ marginLeft: '3px' }}
            className="need-interaction"
          >
            <CloseIcon
              style={{
                height: '30px',
                width: '30px',
                color: theme.palette.common.white,
              }}
            />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          marginTop: '1rem',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <>
          <Box
            style={{
              width: '95%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <label htmlFor="off-task-students" style={{ fontSize: '1.05rem' }}>
              Number of Off-Task Students
            </label>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '1rem',
                minWidth: '230px',
              }}
            >
              <IconButton
                aria-label="Decrement number of students"
                onClick={() => setOffTaskStudentCount(-1)}
                style={{ marginTop: 10 }}
              >
                <ArrowBackIcon style={{ color: theme.palette.obsBlue.dark }} />
              </IconButton>
              <FormControl fullWidth size="small">
                <TextField
                  inputRef={inputRef}
                  placeholder="Enter a number"
                  sx={{ marginTop: '.75rem' }}
                  //label=""
                  id="off-task-students"
                  error={props.offTaskNumberError}
                  size="small"
                  value={props.offTaskNumber}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSave) {
                      props.addToTimeline(false);
                    }
                  }}
                  onChange={(e) => {
                    updateOffTaskCount(e.target.value);
                  }}
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      max: props.studentCount,
                      type: 'number',
                    },

                    inputLabel: { shrink: true }
                  }} />
              </FormControl>
              <IconButton
                aria-label="Increment number of students"
                onClick={() => setOffTaskStudentCount(1)}
                style={{ marginTop: 10 }}
              >
                <ArrowForwardIcon
                  style={{ color: theme.palette.obsBlue.dark }}
                />
              </IconButton>
            </Box>
            <Box
              sx={{
                marginTop: '.5rem',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                width: '85%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <KitButton
                  onClick={(_e) => props.addToTimeline(false)}
                  size="sm"
                  style={{
                    width: '110px',
                    backgroundColor: theme.palette.gold.main,
                    marginTop: '1rem',
                  }}
                  disabled={!canSave}
                >
                  <span>
                    <CheckIcon
                      sx={{
                        marginRight: '.5rem',
                        marginTop: '-5px',
                        width: '16px !important',
                        height: '16px !important',
                      }}
                    />{' '}
                    Save
                  </span>
                </KitButton>
              </Box>
            </Box>
          </Box>
        </>
      </DialogContent>
      {/* <DialogActions sx={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}></DialogActions> */}
    </Dialog>
  );
};

OffTaskDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  closeConfirm: PropTypes.func.isRequired,
  observation: PropTypes.object.isRequired,
  offTaskNumber: PropTypes.string.isRequired,
  setOffTaskNumber: PropTypes.func.isRequired,
  offTaskNumberError: PropTypes.object.isRequired,
  setOffTaskNumberError: PropTypes.func.isRequired,
  addToTimeline: PropTypes.func.isRequired,
  studentCount: PropTypes.number.isRequired,
};

export default OffTaskDialog;
